export type IceCandidateType = "host" | "srflx" | "prflx" | "relay";

export interface IcePathTelemetry {
  localCandidateType: IceCandidateType;
  remoteCandidateType: IceCandidateType;
  protocol?: "udp" | "tcp";
  relayProtocol?: "udp" | "tcp" | "tls";
}

type StatsRecord = {
  id: string;
  type: string;
  selected?: boolean;
  nominated?: boolean;
  state?: string;
  selectedCandidatePairId?: string;
  localCandidateId?: string;
  remoteCandidateId?: string;
  candidateType?: string;
  protocol?: string;
  relayProtocol?: string;
};

const CANDIDATE_TYPES = new Set<IceCandidateType>(["host", "srflx", "prflx", "relay"]);
const PROTOCOLS = new Set<IcePathTelemetry["protocol"]>(["udp", "tcp"]);
const RELAY_PROTOCOLS = new Set<IcePathTelemetry["relayProtocol"]>(["udp", "tcp", "tls"]);

function candidateType(value?: string): IceCandidateType | null {
  const normalized = value?.toLowerCase() as IceCandidateType | undefined;
  return normalized && CANDIDATE_TYPES.has(normalized) ? normalized : null;
}

function selectedPathFromStats(stats: RTCStatsReport): IcePathTelemetry | null {
  const records: StatsRecord[] = [];
  stats.forEach(report => records.push(report as unknown as StatsRecord));
  const byId = new Map(records.map(report => [report.id, report]));
  const transport = records.find(report => report.type === "transport" && report.selectedCandidatePairId);
  const selectedPair = (transport?.selectedCandidatePairId ? byId.get(transport.selectedCandidatePairId) : undefined) ||
    records.find(report => report.type === "candidate-pair" && report.selected) ||
    records.find(report => report.type === "candidate-pair" && report.nominated && report.state === "succeeded");
  if (!selectedPair?.localCandidateId || !selectedPair.remoteCandidateId) return null;

  const localCandidate = byId.get(selectedPair.localCandidateId);
  const remoteCandidate = byId.get(selectedPair.remoteCandidateId);
  const localCandidateType = candidateType(localCandidate?.candidateType);
  const remoteCandidateType = candidateType(remoteCandidate?.candidateType);
  if (!localCandidateType || !remoteCandidateType) return null;

  const rawProtocol = (localCandidate?.protocol || remoteCandidate?.protocol || "").toLowerCase() as IcePathTelemetry["protocol"];
  const rawRelayProtocol = (localCandidate?.relayProtocol || remoteCandidate?.relayProtocol || "").toLowerCase() as IcePathTelemetry["relayProtocol"];
  return {
    localCandidateType,
    remoteCandidateType,
    ...(rawProtocol && PROTOCOLS.has(rawProtocol) ? { protocol: rawProtocol } : {}),
    ...(rawRelayProtocol && RELAY_PROTOCOLS.has(rawRelayProtocol) ? { relayProtocol: rawRelayProtocol } : {})
  };
}

export async function readSelectedIcePath(
  peerConnection: RTCPeerConnection,
  timeoutMs = 8_000
): Promise<IcePathTelemetry | null> {
  const deadline = Date.now() + timeoutMs;
  do {
    if (peerConnection.connectionState === "failed" || peerConnection.connectionState === "closed") return null;
    try {
      const selectedPath = selectedPathFromStats(await peerConnection.getStats());
      if (selectedPath) return selectedPath;
    } catch {
      // The connection may close between the state check and getStats().
    }
    await new Promise(resolve => window.setTimeout(resolve, 400));
  } while (Date.now() < deadline);
  return null;
}
